import { getSizedImageUrl } from '@shopify/theme-images';
import getProgressiveJpgUrl from '../utils/get-progressive-jpg-url.js';

/** @param {number | string} price */
const convertToShopifyPrice = price => {
  if (typeof price === 'string') {
    if (price.includes('.')) {
      return Number(price) * 100;
    }

    return Number(price);
  }

  return price;
};

class Product {
  constructor(product) {
    this.product = product;
  }

  get featuredImage() {
    return this.product.featured_image
      ? this.product.featured_image
      : this.images[0];
  }

  get handle() {
    return this.product.handle;
  }

  get id() {
    return this.product.id || this.product.product_id;
  }

  get images() {
    if (!this.product.images) {
      return [this.product.image];
    }

    return this.product.images.map(url => url.replace('_large.', '.'));
  }

  sizedProgressiveImage(width = 250) {
    if (!this.featuredImage) {
      return window.settings.placeholder_images.product;
    }

    if (this.product.model_source === 'swym') {
      return this.featuredImageFromSwym();
    }

    return getProgressiveJpgUrl(
      getSizedImageUrl(this.featuredImage, `${width}x`)
    );
  }

  featuredImageFromSwym() {
    const url = new URL(this.featuredImage);
    const params = new URLSearchParams(url.search);

    params.delete('crop');
    url.search = params;

    return url.toString();
  }

  get minimumPrice() {
    return Math.min(...this.prices);
  }

  get compareAtPrice() {
    const prices = this.product.variants.filter(
      variant => variant.compare_at_price
    );

    if (prices.length === 0) {
      return null;
    }

    return Math.min(
      ...prices.map(variant => convertToShopifyPrice(variant.compare_at_price))
    );
  }

  get price() {
    const prices = this.product.variants.filter(variant => variant.price);

    if (prices.length === 0) {
      return null;
    }

    return Math.min(
      ...prices.map(variant => convertToShopifyPrice(variant.price))
    );
  }

  get options() {
    return this.buildOptions(true);
  }

  get availableOptions() {
    return this.buildOptions(false);
  }

  get optionsWithUniqueValues() {
    const collatedOptions = [];

    this.optionNames.forEach((name, i) => {
      const options = new Set([]);
      this.variants.forEach(variant => {
        options.add(variant[`option${i + 1}`]);
      });

      collatedOptions.push([name, [...options]]);
    });

    return Object.fromEntries(collatedOptions);
  }

  firstVariantWithOption(name, value) {
    return this.variants.find(variant => {
      const optionIndex = this.optionNames.indexOf(name);
      return variant[`option${optionIndex + 1}`] === value;
    });
  }

  get variantQuantities() {
    return this.product.variants.map(variant => {
      return {
        id: variant.id,
        quantity: variant.quantity,
      };
    });
  }

  get stockLabel() {
    if (this.normalisedTags.includes('sold out')) {
      return 'Sold Out';
    }

    if (this.normalisedTags.includes('coming soon')) {
      return 'Coming Soon';
    }

    const anyAvailableVariants =
      [...this.product.variants].filter(v => v.available && v.quantity > 0)
        .length > 0;

    if (anyAvailableVariants) {
      return 'In Stock';
    }

    const anyOversellVariants =
      [...this.product.variants].filter(v => v.available && v.quantity <= 0)
        .length > 0;

    if (anyOversellVariants) {
      return 'Pre-order';
    }

    return 'Out of stock';
  }

  /**
   * Build an options object with each value for each option (e.g., "Size").
   * Optionally, variants with options that are unavailable can be excluded.
   *
   * @param includeUnavailable
   *
   * @returns {{values: string[], name: Option, position: number}[]|Option[]}
   */
  buildOptions(includeUnavailable = true) {
    const { options } = this.product;

    if (typeof options[0] === 'string') {
      return options.map((option, index) => {
        const position = index + 1;
        let { variants } = this.product;

        if (!includeUnavailable) {
          variants = variants.filter(variant => variant.available);
        }

        return {
          name: option,
          position,
          values: variants.map(variant => variant[`option${position}`]),
        };
      });
    }

    return options;
  }

  get prices() {
    return this.product.variants.map(variant => {
      const price = variant.price || variant.compare_at_price;
      if (typeof price === 'string') {
        return Number(price) * 100;
      }

      return price;
    });
  }

  get tags() {
    if (Array.isArray(this.product.tags)) {
      return this.product.tags;
    }

    return this.product.tags.split(', ');
  }

  get normalisedTags() {
    return this.tags.map(tag => tag.toLowerCase());
  }

  get title() {
    return this.product.title;
  }

  get vendor() {
    return this.product.vendor;
  }

  get url() {
    return `/products/${this.handle}`;
  }

  get variants() {
    return this.product.variants;
  }

  getValuesForOption(option) {
    const variantOption = this.availableOptions.find(
      opt => opt.name.toLowerCase() === option.toLowerCase()
    );

    if (!variantOption) {
      return [];
    }

    const values = variantOption.values.map(value => value.toLowerCase());

    return [...new Set(values)];
  }

  getTagsByPrefix(prefix) {
    return this.tags
      .filter(tag => new RegExp(`^${prefix.toLowerCase()}:`).test(tag))
      .map(tag =>
        tag.replace(new RegExp(`^${prefix.toLowerCase()}:`, 'g'), '')
      );
  }

  getUrlWithinCollection(collectionHandle) {
    const route = this.url;

    if (collectionHandle) {
      return `/collections/${collectionHandle}${route}`;
    }

    return route;
  }

  applyFilter(filter, values) {
    /** @type {('every' | 'some')} */
    let arrayMethod = 'some';
    if (filter.filterType === 'AND') {
      arrayMethod = 'every';
    }

    if (filter.sourceType === 'price') {
      return values[arrayMethod](range => this.priceLiesWithinRange(range));
    }

    return values[arrayMethod](value =>
      this.getValuesForFilter(filter).includes(value)
    );
  }

  getValuesForFilter(filter) {
    const sourceTypeMethod = {
      option: this.getValuesForOption,
      price: () => [this.minimumPrice],
      tag: this.getTagsByPrefix,
    }[filter.sourceType];

    return sourceTypeMethod.call(this, filter.sourceName);
  }

  priceLiesWithinRange(range) {
    const [min, max] = range
      .replace(/[^0-9–]*/g, '')
      .split('–')
      .map(price => Number(price) * 100);

    if (this.minimumPrice <= min) {
      return false;
    }

    if (max && this.minimumPrice >= max) {
      return false;
    }

    return true;
  }

  get features() {
    const features = [];

    this.tags.forEach(tag => {
      if (tag.includes('PLP_Feature-')) {
        const parts = tag.replace('PLP_Feature-', '').split(':');
        features.push({
          name: parts[0],
          value: parts[1],
        });
      }
    });

    return features;
  }

  get countVariantOptions() {
    const uniqueOptions = { 0: [], 1: [], 2: [] };
    const optionNames = this.product.options;
    [...this.variants].forEach(variant => {
      variant.options.forEach((option, i) => {
        uniqueOptions[i].push(option);
      });
    });

    Object.values(uniqueOptions).forEach((list, i) => {
      uniqueOptions[i] = new Set(list);
    });

    if (optionNames.length < 1) {
      return null;
    }

    const optionIndex = optionNames.includes('Colour')
      ? optionNames.indexOf('Colour')
      : 0;

    return {
      name: optionNames[optionIndex],
      count: uniqueOptions[optionIndex].size,
    };
  }

  /** @return {Array} */
  get optionNames() {
    if (this.product.model_source === 'searchanise') {
      return Object.keys(this.variants[0].options);
    }

    if (this.product.model_source === 'shopify') {
      return this.product.options;
    }

    return [];
  }

  get variantCountLabel() {
    try {
      const count = this.countVariantOptions;

      if (!count) {
        return null;
      }

      if (!['Frame Size', 'Colour'].includes(count.name)) {
        return null;
      }

      return `${count.count} ${count.name}s`;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);

      return null;
    }
  }

  get hasOnlyDefaultVariant() {
    return this.variants[0].option1 === 'Default Title';
  }

  static fromShopify(product) {
    return new Product({
      ...product,
      variants: product.variants.map(variant => {
        return {
          ...variant,
          image: variant.featured_image?.src,
        };
      }),
      model_source: 'shopify',
    });
  }

  static fromSearchanise(product) {
    return new Product({
      ...product,
      model_source: 'searchanise',
      id: Number(product.product_id),
      images: product.shopify_images,
      handle: product.link.split('products/').slice(-1).pop(),
      tags: product.tags.split('[:ATTR:]'),
      variants: product.shopify_variants.map(variant => {
        const optionValues = Object.values(variant.options);

        // -1 = available: out of stock but oversells
        //  0 = unavailable: out of stock
        //  1 = available: in stock
        const availableAndInStock = variant.available === '1';
        const availableOrOversells = ['-1', '1'].includes(variant.available);

        return {
          ...variant,
          id: Number(variant.variant_id),
          available: availableOrOversells,
          available_raw: variant.available,
          // We don't have an actual stock level, so we'll just
          // set it to 1 if it can be sold.
          quantity: availableAndInStock ? 1 : 0,
          image: variant.image_link.replace('_large.', '.'),
          compare_at_price: variant.list_price,
          options: variant.options,
          option1: optionValues[0],
          option2: optionValues[1],
          option3: optionValues[2],
        };
      }),
    });
  }

  static fromSwym(product) {
    return new Product({
      ...product,
      original_swym_data: product,
      model_source: 'swym',
      id: Number(product.empi),
      title: product.dt,
      url: product.du,
      handle: product.du.split('products/')[1],
      images: [product.iu],
      price: product.pr * 100,
      type: product.ct,
      tags: [],
      variants: [
        {
          id: Number(product.epi),
          title: product.dt,
          available: true,
          inventory_management: 'shopify',
          price: product.pr * 100,
        },
      ],
    });
  }
}

export default Product;
